import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import ChatInput from "./ChatInput";
import { Dropdown, Button } from "react-bootstrap";
import io from "socket.io-client";
import ChatContacts from "./ChatContacts";
import { useParams } from "react-router-dom";
import { PropagateLoader } from "react-spinners";
import { toast } from "react-toastify";
import { getMessageDayStatus } from "../../utilities/functions";
import { validateChatAttachment } from "./chatAttachmentConfig";

interface Message {
  id_message?: number;
  id_chat?: number;
  id_sender?: number;
  id_received: number;
  content: string;
  time: string;
  is_right: boolean;
  nom_user?: string;
  prenom_user?: string;
  img?: string;

}

interface Contact {
  id_user: number;
  nom_user: string;
  prenom_user: string;
  username_user: string;
  position: string;
  phone_number: string;
  email_user: string;
  status: string;
  img: string;
}

interface AttachmentPayload {
  type: "file";
  name: string;
  url: string;
  mimeType?: string;
  size?: number;
  text?: string;
}

const CHAT_ATTACHMENT_PREFIX = "__CHAT_FILE__:";
const DEFAULT_USER_IMAGE = "asset/images/logo.png";

const AvatarImage: React.FC<{
  src: string;
  alt: string;
  size: number;
  fallback: React.ReactNode;
}> = ({ src, alt, size, fallback }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return <>{fallback}</>;
  }

  return (
    <img
      src={src}
      alt={alt}
      className="rounded-circle border border-light shadow-sm"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        objectFit: "cover",
      }}
      onError={() => setHasError(true)}
    />
  );
};

const normalizeContactPayload = (payload: any): Contact | null => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  return {
    id_user: Number(payload.id_user || 0),
    nom_user: String(payload.nom_user ?? payload.first_name ?? ""),
    prenom_user: String(payload.prenom_user ?? payload.last_name ?? ""),
    username_user: String(payload.username_user ?? payload.username ?? ""),
    position: String(payload.position ?? payload.user_type ?? ""),
    phone_number: String(payload.phone_number ?? payload.phone_user ?? payload.phone ?? ""),
    email_user: String(payload.email_user ?? payload.email ?? ""),
    status: String(payload.status ?? payload.chat_status ?? ""),
    img: String(payload.img ?? DEFAULT_USER_IMAGE),
  };
};

const normalizeMessagesPayload = (
  payload: unknown,
  currentUserId = 0
): Message[] => {
  if (Array.isArray(payload)) {
    return payload.map((message) => {
      const normalizedMessage = message as Message & {
        first_name?: string;
        last_name?: string;
        username?: string;
        id_ticket?: number;
      };

      if (
        message &&
        typeof message === "object" &&
        "id_ticket" in message &&
        !("id_chat" in message)
      ) {
        normalizedMessage.id_chat = normalizedMessage.id_ticket;
      }

      return {
        ...normalizedMessage,
        id_sender: Number(normalizedMessage.id_sender || 0),
        id_received: Number(normalizedMessage.id_received || 0),
        is_right:
          currentUserId > 0
            ? Number(normalizedMessage.id_sender) === currentUserId
            : Boolean(normalizedMessage.is_right),
        nom_user: String(
          normalizedMessage.nom_user ?? normalizedMessage.first_name ?? ""
        ),
        prenom_user: String(
          normalizedMessage.prenom_user ?? normalizedMessage.last_name ?? ""
        ),
        img: String(normalizedMessage.img ?? DEFAULT_USER_IMAGE),
      };
    });
  }

  console.error("Unexpected messages payload:", payload);
  return [];
};

const parseAttachmentPayload = (content: string): AttachmentPayload | null => {
  if (!content.startsWith(CHAT_ATTACHMENT_PREFIX)) {
    return null;
  }

  try {
    const parsedContent = JSON.parse(
      content.slice(CHAT_ATTACHMENT_PREFIX.length)
    ) as AttachmentPayload;

    if (
      parsedContent &&
      parsedContent.type === "file" &&
      typeof parsedContent.name === "string" &&
      typeof parsedContent.url === "string"
    ) {
      return parsedContent;
    }
  } catch (error) {
    console.error("Unable to parse attachment payload:", error);
  }

  return null;
};

const formatFileSize = (size?: number) => {
  if (!size) {
    return "";
  }

  const units = ["B", "KB", "MB", "GB"];
  let currentSize = size;
  let unitIndex = 0;

  while (currentSize >= 1024 && unitIndex < units.length - 1) {
    currentSize /= 1024;
    unitIndex += 1;
  }

  return `${currentSize.toFixed(currentSize >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const ChatHistory: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const visibleMessages = Array.isArray(messages) ? messages : [];

  const id_user = parseInt(localStorage.getItem("userid") ?? "0");

  const params = useParams<{ id_received?: string; id_chat?: string }>();

  const chatId = parseInt(params.id_chat ?? "0");

  const [idReceived, setIdReceived] = useState<number>(
    parseInt(params.id_received ?? "0")
  );

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const socketUrl = process.env.REACT_APP_SOCKET_URL ?? "http://localhost:5002";

  const [contact, setContact] = useState<Contact | undefined>(undefined);
  const [isSidebarRightOpen, setIsSidebarRightOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const currentUser = useMemo(() => {
    const rawUser = localStorage.getItem("user");

    if (!rawUser) {
      return undefined;
    }

    try {
      const parsed = JSON.parse(rawUser);
      return normalizeContactPayload({
        id_user: parsed.id_user,
        first_name: parsed.first_name,
        last_name: parsed.last_name,
        username: parsed.username,
        email: parsed.email,
        phone: parsed.phone,
        img: parsed.img,
      });
    } catch {
      return undefined;
    }
  }, []);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatBodyRef = useRef<HTMLDivElement>(null);
  const didInitialScrollRef = useRef(false);
  const socketRef = useRef(
    io(socketUrl, {
      transports: ["websocket"],
      withCredentials: true,
    })
  );

  const activeContactId = useMemo(() => {
    if (idReceived && idReceived !== id_user) {
      return idReceived;
    }

    const peerMessage = visibleMessages.find((message) => {
      const senderId = Number(message.id_sender || 0);
      const receiverId = Number(message.id_received || 0);
      return senderId !== id_user || receiverId !== id_user;
    });

    if (!peerMessage) {
      return idReceived || 0;
    }

    const senderId = Number(peerMessage.id_sender || 0);
    const receiverId = Number(peerMessage.id_received || 0);
    return senderId !== id_user ? senderId : receiverId;
  }, [idReceived, id_user, visibleMessages]);

  const scrollToBottom = (behavior: ScrollBehavior = "auto") => {
    const chatBody = chatBodyRef.current;
    if (!chatBody) return;

    chatBody.scrollTo({
      top: chatBody.scrollHeight,
      behavior,
    });
  };

  const keepChatPinnedToBottom = () => {
    window.requestAnimationFrame(() => {
      scrollToBottom("auto");
    });

    window.setTimeout(() => {
      scrollToBottom("auto");
    }, 50);

    window.setTimeout(() => {
      scrollToBottom("smooth");
    }, 150);
  };

  const scrollToBottomAfterRender = (behavior: ScrollBehavior = "smooth") => {
    window.requestAnimationFrame(() => {
      scrollToBottom(behavior);
    });

    window.setTimeout(() => {
      scrollToBottom(behavior);
    }, 80);
  };

  const resolveAttachmentUrl = (fileUrl: string) => {
    if (/^https?:\/\//i.test(fileUrl)) {
      return fileUrl;
    }

    const baseUrl = (backendUrl ?? window.location.origin).replace(/\/$/, "");
    return `${baseUrl}${fileUrl.startsWith("/") ? fileUrl : `/${fileUrl}`}`;
  };

  const getInitials = (person?: Partial<Contact | Message>) => {
    const firstName = person?.nom_user?.trim()?.[0] ?? "";
    const lastName = person?.prenom_user?.trim()?.[0] ?? "";
    const username =
      "username_user" in (person || {})
        ? (person as Partial<Contact>)?.username_user?.trim()?.[0] ?? ""
        : "";
    return `${firstName}${lastName}`.toUpperCase() || username.toUpperCase() || "A";
  };

  const getPersonDisplayName = (person?: Partial<Contact | Message>) => {
    const fullName = `${person?.nom_user || ""} ${
      person?.prenom_user || ""
    }`.trim();

    if (fullName) return fullName;

    if (person && "username_user" in person && person.username_user) {
      return person.username_user;
    }

    if (person && "email_user" in person && person.email_user) {
      return person.email_user;
    }

    return "Discussion AIDA";
  };

  const getMessageAvatarPerson = (message: Message) => {
    if (message.is_right) {
      return {
        nom_user: currentUser?.nom_user || message.nom_user || "",
        prenom_user: currentUser?.prenom_user || message.prenom_user || "",
        username_user: currentUser?.username_user || "",
        img: currentUser?.img || message.img || DEFAULT_USER_IMAGE,
      };
    }

    return {
      nom_user: message.nom_user || contact?.nom_user || "",
      prenom_user: message.prenom_user || contact?.prenom_user || "",
      username_user: contact?.username_user || "",
      img: message.img || contact?.img || DEFAULT_USER_IMAGE,
    };
  };

  const resolveUserImageUrl = (image?: string) => {
    if (!image || image === DEFAULT_USER_IMAGE) {
      return "";
    }

    if (/^https?:\/\//i.test(image)) {
      return image;
    }

    const cleanImage = image
      .replace(/^(\.\.\/)+/, "")
      .replace(/^react\/public\//, "")
      .replace(/^public\//, "")
      .replace(/^\/+/, "");

    if (!cleanImage || cleanImage === DEFAULT_USER_IMAGE) {
      return "";
    }

    if (cleanImage.startsWith("asset/")) {
      return `/${cleanImage}`;
    }

    const baseUrl = (backendUrl ?? window.location.origin).replace(/\/$/, "");
    return `${baseUrl}/${cleanImage}`;
  };

  const renderAvatar = (
    person: Partial<Contact | Message> | undefined,
    size = 40
  ) => {
    const imageUrl = resolveUserImageUrl(person?.img);
    const fallback = (
      <div
        className={`rounded-circle d-flex align-items-center justify-content-center text-white fw-bold ${getColorByLetter(
          getInitials(person)[0] || "A"
        )}`}
        style={{ width: `${size}px`, height: `${size}px` }}
      >
        {getInitials(person)}
      </div>
    );

    if (imageUrl) {
      return (
        <AvatarImage
          src={imageUrl}
          alt="Avatar"
          size={size}
          fallback={fallback}
        />
      );
    }

    return fallback;
  };

  const getContactById = async (contactId: number) => {
    if (!contactId) {
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/chats/user/${contactId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Impossible de recuperer le contact.");
      }

      const data = normalizeContactPayload(await response.json());

      if (data) {
        setContact(data);
      }
    } catch (error) {
      console.error("Unable to load active chat contact:", error);
    }
  };

  useEffect(() => {
    if (!chatId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    if (idReceived) {
      void getConversationMessages(chatId, idReceived);
      return;
    }

    void getMessages(chatId);
  }, [chatId, idReceived]);

  useEffect(() => {
    if (params.id_received) {
      setIdReceived(parseInt(params.id_received));
    }
  }, [params.id_received]);

  useEffect(() => {
    if (activeContactId && contact?.id_user !== activeContactId) {
      void getContactById(activeContactId);
    }
  }, [activeContactId, contact?.id_user]);

  useEffect(() => {
    if (contact || !activeContactId || visibleMessages.length === 0) {
      return;
    }

    const messageContact = visibleMessages.find(
      (message) =>
        message.id_sender === activeContactId ||
        (!message.is_right && message.nom_user && message.prenom_user)
    );

    if (!messageContact?.nom_user) {
      return;
    }

    setContact({
      id_user: activeContactId,
      nom_user: messageContact.nom_user,
      prenom_user: messageContact.prenom_user ?? "",
      username_user: "",
      position: "",
      phone_number: "",
      email_user: "",
      status: "",
      img: messageContact.img ?? DEFAULT_USER_IMAGE,
    });
  }, [activeContactId, contact, visibleMessages]);

  useEffect(() => {
    didInitialScrollRef.current = false;
  }, [chatId, idReceived]);

  useEffect(() => {
    const socket = socketRef.current;

    socket.on("connect", () => {
      // console.log('Successfully connected to the server');
      socket.emit("registerUser", id_user);
    });

    // Recevoir des messages du serveur
    socket.on("receive_message", (newMessage: Message) => {
      newMessage = normalizeMessagesPayload([newMessage], id_user)[0];

      if (newMessage.id_sender && newMessage.id_sender !== id_user) {
        setMessages((prevMessages) => [
          ...normalizeMessagesPayload(prevMessages, id_user),
          newMessage,
        ]);

        keepChatPinnedToBottom();
      }
    });

    return () => {
      socket.off("receive_message");
    };
  }, [id_user, socketRef]);

  useLayoutEffect(() => {
    if (loading || visibleMessages.length === 0) return;

    keepChatPinnedToBottom();
  }, [chatId, idReceived, loading, visibleMessages.length]);
  const sendMessageThroughSocket = (content: string) => {
    const newMessage: Message = {
      content,
      time: new Date().toISOString(),
      is_right: false,
      id_sender: id_user ?? 0,
      id_received: activeContactId ?? 0,
      id_chat: chatId,
    };

    const socket = socketRef.current;
    socket.emit("send_private_message", newMessage);

    const outgoingMessage = { ...newMessage, is_right: true };

    setMessages((prevMessages) => [
      ...normalizeMessagesPayload(prevMessages, id_user),
      outgoingMessage,
    ]);

    socket.emit("message", { ...outgoingMessage, contact });

    keepChatPinnedToBottom();

    scrollToBottomAfterRender("smooth");
  };
  const uploadAttachment = async (file: File) => {
    const validationError = validateChatAttachment(file);

    if (validationError) {
      throw new Error(validationError);
    }

    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(`${backendUrl}/api/chat/upload`, {
      method: "POST",
      body: formData,
      mode: "cors",
    });

    const payload = (await response.json().catch(() => null)) as
      | {
          error?: string;
          fileUrl?: string;
          originalName?: string;
          mimeType?: string;
          size?: number;
        }
      | null;

    if (!response.ok || !payload?.fileUrl || !payload?.originalName) {
      throw new Error(
        payload?.error ?? "Impossible de televerser le fichier."
      );
    }

    return payload as {
      fileUrl: string;
      originalName: string;
      mimeType?: string;
      size?: number;
    };
  };

  const handleSendMessage = async (content: string, file?: File | null) => {
    const trimmedContent = content.trim();

    if ((!trimmedContent && !file) || !chatId || !activeContactId) {
      if (!chatId || !activeContactId) {
        toast.warn("Aucune conversation active.");
      }
      return;
    }

    setIsSending(true);

    try {
      let payloadContent = trimmedContent;

      if (file) {
        const uploadedFile = await uploadAttachment(file);
        payloadContent = `${CHAT_ATTACHMENT_PREFIX}${JSON.stringify({
          type: "file",
          name: uploadedFile.originalName ?? file.name,
          url: uploadedFile.fileUrl,
          mimeType: uploadedFile.mimeType ?? file.type,
          size: uploadedFile.size ?? file.size,
          text: trimmedContent,
        } satisfies AttachmentPayload)}`;
      }

      sendMessageThroughSocket(payloadContent);
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Impossible d'envoyer le message."
      );
      throw error;
    } finally {
      setIsSending(false);
    }
  };

  const handleContactClick = (contact: Contact) => {
    if (contact) {
      setIdReceived(contact.id_user); // ✅ correct
      setContact(contact);
    }
  };

  const toggleSidebarRight = () => {
    setIsSidebarRightOpen(!isSidebarRightOpen);
  };

  const getMessages = async (id_chat: number) => {
    try {
      setLoading(true);

      const bodyData = JSON.stringify({
        id_chat,
      });

      const messagesResponse = await fetch(`${backendUrl}/api/chat/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: bodyData,
        mode: "cors",
      });

      const payload = await messagesResponse.json();
      const data = normalizeMessagesPayload(payload, id_user);

      // console.log("data");
      // console.log(data);

      setMessages(data);

      return data;
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };


  const getConversationMessages = async (id_chat: number, id_received: number) => {
    try {
      setLoading(true);

      const bodyData = JSON.stringify({
        id_chat,
        id_received
      });

      const messagesResponse = await fetch(`${backendUrl}/api/chat/contact/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: bodyData,
        mode: "cors",
      });

      const payload = await messagesResponse.json();
      const data = normalizeMessagesPayload(payload, id_user);

      // console.log("data");
      // console.log(data);

      setMessages(data);

      return data;
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);


  // getColorByLetter.ts
  const colors = [
    "bg-primary",
    "bg-success",
    "bg-danger",
    "bg-warning",
    "bg-info",
    "bg-secondary",
    "bg-dark",
  ] as const;

  type BootstrapBgClass = (typeof colors)[number];

  function getColorByLetter(letter: string): BootstrapBgClass {
    const charCode = letter.toUpperCase().charCodeAt(0); // 'A' => 65
    const index = (charCode - 65) % colors.length;
    return colors[index];
  }


  return (
    <>
      <style type="text/css">
        {`
          .dropdown-toggle::after {
            display: none !important;
          }
        `}
      </style>
      <ChatContacts
        handleContactClick={handleContactClick}
        id_contact={id_user}
        activeContactId={activeContactId}
        isSidebarOpen={isSidebarOpen}
        onToggleSidebar={toggleSidebar}
      />
      <div
        className="col app-chat-history"
        style={{
          padding: "0",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        }}
      >
        <div
          className="chat-history-wrapper"
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            height: "100%",
            minHeight: 0,
          }}
        >
          <div
            className="chat-history-header border-bottom"
            style={{ backgroundColor: "#fff" }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex overflow-hidden align-items-center">
                <i className="ti ti-menu-2 ti-lg cursor-pointer d-lg-none d-block me-4"></i>
                <i
                  className="la la-list-alt la-2x ti-lg cursor-pointer me-4"
                  onClick={toggleSidebar}
                />
                {contact ? (
                  <div
                    className="d-flex align-items-center"
                    onClick={toggleSidebarRight}
                  >
                    {renderAvatar(contact)}
                    <div className="chat-contact-info flex-grow-1 ms-4">
                      <h6 className="m-0 fw-normal">
                        {getPersonDisplayName(contact)}
                      </h6>
                      {contact.status && (
                        <small className="user-status content-body">
                          {contact.status}
                        </small>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <h6 className="m-0 fw-semibold">Selectionnez une discussion</h6>
                    <small className="text-muted">
                      Choisissez un contact dans le sidebar.
                    </small>
                  </div>
                )}
              </div>
              <div className="d-flex align-items-center">
                <i
                  className="las la-phone-alt cursor-pointer"
                  title="Appel audio"
                  style={{
                    transition: "background-color 0.3s ease, color 0.3s ease",
                    padding: "10px",
                    borderRadius: "50%",
                    fontSize: "20px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#fff4bf";
                    e.currentTarget.style.color = "#ffd02a";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "";
                  }}
                >
                  {" "}
                </i>
                <i
                  className="las la-video cursor-pointer "
                  title="Appel video"
                  style={{
                    transition: "background-color 0.3s ease, color 0.3s ease",
                    padding: "10px",
                    borderRadius: "50%",
                    fontSize: "20px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#fff4bf";
                    e.currentTarget.style.color = "#ffd02a";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "";
                  }}
                ></i>
                <i
                  className="las la-search"
                  title="Rechercher dans la discussion"
                  style={{
                    transition: "background-color 0.3s ease, color 0.3s ease",
                    padding: "10px",
                    borderRadius: "50%",
                    fontSize: "20px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#fff4bf";
                    e.currentTarget.style.color = "#ffd02a";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "";
                  }}
                ></i>

                <Dropdown>
                  <Dropdown.Toggle
                    as={Button}
                    variant="light"
                    className="icon-wrapper"
                    style={{
                      padding: "0",
                      borderRadius: "100%",
                      fontSize: "20px",
                      border: "none",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#fff4bf";
                      e.currentTarget.style.color = "#ffd02a";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = "";
                    }}
                  >
                    <i className="las la-ellipsis-v"></i>
                  </Dropdown.Toggle>

                  <Dropdown.Menu align="end">
                    <Dropdown.Item href="#" onClick={toggleSidebarRight}>
                      Voir le contact
                    </Dropdown.Item>
                    <Dropdown.Item href="#">Notifications silencieuses</Dropdown.Item>
                    <Dropdown.Item href="#">Bloquer le contact</Dropdown.Item>
                    <Dropdown.Item href="#">Vider la discussion</Dropdown.Item>
                    <Dropdown.Item href="#">Signaler</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
          </div>

          <div
            className="chat-history-body"
            ref={chatBodyRef}
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              height: "auto",
              minHeight: 0,
              overflowY: "auto",
            }}
          >
            <ul
              className="list-unstyled chat-history"
              style={{ height: "100%", marginBottom: 0 }}
            >
              {loading ? (
                <div style={{ textAlign: "center" }}>
                  <p>
                    <PropagateLoader
                      color={"#123abc"}
                      loading={loading}
                      size={20}
                    />
                  </p>
                </div>
              ) : visibleMessages.length > 0 ? (
                visibleMessages.map((message) => {
                  const attachment = parseAttachmentPayload(message.content);
                  const attachmentUrl = attachment
                    ? resolveAttachmentUrl(attachment.url)
                    : "";

                  return (
                    <li
                      key={
                        message.id_message ??
                        `${message.id_sender}-${message.time}`
                      }
                      className={`chat-message ${message.is_right
                        ? "chat-message-right"
                        : "chat-message-left"
                        }`}
                      style={{ marginBottom: "0px" }}
                    >
                      <div className="d-flex overflow-hidden align-items-end">
                        {!message.is_right ? (
                          <div className="user-avatar flex-shrink-0">
                            {renderAvatar(getMessageAvatarPerson(message))}
                          </div>
                        ) : null}
                        <div
                          className="chat-message-wrapper flex-grow-1"
                          style={{ margin: "0 10px" }}
                        >
                          <div className="chat-message-content">
                            {attachment ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "0.5rem",
                                }}
                              >
                                {attachment.text ? (
                                  <p
                                    className={`mb-0 ${message.is_right ? "is-right" : "is-left"
                                      }`}
                                  >
                                    {attachment.text}
                                  </p>
                                ) : null}
                                <a
                                  href={attachmentUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: "0.75rem",
                                    padding: "0.85rem 1rem",
                                    borderRadius: "0.9rem",
                                    background: message.is_right
                                      ? "#e9f2ff"
                                      : "#f4f5f7",
                                    color: "#111827",
                                    textDecoration: "none",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.75rem",
                                      minWidth: 0,
                                    }}
                                  >
                                    <i
                                      className="las la-paperclip"
                                      style={{ fontSize: "1.2rem" }}
                                    ></i>
                                    <div
                                      style={{
                                        minWidth: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                      }}
                                    >
                                      <strong
                                        style={{
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {attachment.name}
                                      </strong>
                                      <small style={{ color: "#6b7280" }}>
                                        {[attachment.mimeType, formatFileSize(attachment.size)]
                                          .filter(Boolean)
                                          .join(" | ")}
                                      </small>
                                    </div>
                                  </div>
                                  <i className="las la-download"></i>
                                </a>
                              </div>
                            ) : (
                              <p
                                className={`mb-1 ${message.is_right ? "is-right" : "is-left"
                                  }`}
                              >
                                {message.content}
                              </p>
                            )}
                          </div>
                          <div
                            className={`content-${message.is_right ? "end" : "start"
                              } content-muted mt-1`}
                          >
                            {message.is_right ? (
                              <i
                                className={`ti ti-checks ti-16px me-1 content-success`}
                              ></i>
                            ) : (
                              ""
                            )}
                            <small style={{ color: "#000" }}>
                              {<i className="las la-check-double me-1"></i>}{" "}
                              {getMessageDayStatus(message.time)}
                            </small>
                          </div>
                        </div>
                        {message.is_right ? (
                          <div className="user-avatar flex-shrink-0">
                            {renderAvatar(getMessageAvatarPerson(message))}
                          </div>
                        ) : null}
                      </div>
                    </li>
                  );
                })
              ) : (
                <div
                  className="no-messages d-flex justify-content-center align-items-center"
                  style={{
                    textAlign: "center",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <i className="las la-comments mb-2" style={{ fontSize: 36 }}></i>
                  <span>Aucun message disponible</span>
                  <small className="text-muted">
                    Envoyez un premier message pour demarrer la discussion.
                  </small>
                </div>
              )}
            </ul>

            <div ref={chatEndRef} />
          </div>

          <div
            className="chat-history-footer"
            style={{
              margin: 0,
              marginTop: "auto",
              padding: "0.75rem 1rem",
              borderTop: "1px solid #e9ecef",
              backgroundColor: "#fff",
              flexShrink: 0,
              position: "sticky",
              bottom: 0,
              zIndex: 1,
            }}
          >
            <ChatInput
              onSendMessage={handleSendMessage}
              isSending={isSending}
            />
          </div>
        </div>
        <div
          className={`col app-chat-sidebar-right app-sidebar overflow-hidden ${isSidebarRightOpen ? "show sidebar-right-open" : ""
            }`}
          id="app-chat-sidebar-right"
          style={{ background: "#fff", border: "1px solid #ddd" }}
        >
          <div
            className="sidebar-header d-flex flex-column justify-content-center align-items-center flex-wrap px-6 pt-12"
            onClick={toggleSidebarRight}
            style={{ margin: "15px 0", height: "108px !important" }}
          >
            <div
              className="avatar avatar-xl avatar-online chat-sidebar-avatar"
              onClick={toggleSidebarRight}
            >
              {renderAvatar(contact, 64)}
            </div>
            <h5 className="mt-4 mb-0">
              {contact
                ? `${contact.nom_user} ${contact.prenom_user}`
                : "Aucun contact"}
            </h5>
            <span>{contact?.position || "Selectionnez une discussion"}</span>
            <i
              className="ti ti-x ti-lg cursor-pointer close-sidebar"
              onClick={toggleSidebarRight}
            ></i>
          </div>
          <div className="sidebar-body p-6 pt-0">
            <div className="my-6">
              <p className="content-uppercase mb-1 content-muted">Profil</p>
              <p className="mb-0">
                {contact?.status || "Les informations du contact apparaitront ici."}
              </p>
            </div>
            <div className="my-6">
              <p className="content-uppercase mb-1 content-muted">
                Personal Information
              </p>
              <ul className="list-unstyled d-grid gap-4 mb-0 ms-2 py-2 content-heading">
                <li className="d-flex align-items-center">
                  <i className="ti ti-mail ti-md"></i>
                  <span className="align-middle ms-2">
                    {contact?.email_user || "Email non renseigne"}
                  </span>
                </li>
                <li className="d-flex align-items-center">
                  <i className="ti ti-phone-call ti-md"></i>
                  <span className="align-middle ms-2">
                    {contact?.phone_number || "Telephone non renseigne"}
                  </span>
                </li>
                <li className="d-flex align-items-center">
                  <i className="ti ti-clock ti-md"></i>
                  <span className="align-middle ms-2">
                    {contact?.username_user || "Utilisateur AIDA"}
                  </span>
                </li>
              </ul>
            </div>
            <div className="my-6">
              <p className="content-uppercase content-muted mb-1">Options</p>
              <ul className="list-unstyled d-grid gap-4 ms-2 py-2 content-heading">
                <li className="cursor-pointer d-flex align-items-center">
                  <i className="ti ti-badge ti-md"></i>
                  <span className="align-middle ms-2">Ajouter une etiquette</span>
                </li>
                <li className="cursor-pointer d-flex align-items-center">
                  <i className="ti ti-star ti-md"></i>
                  <span className="align-middle ms-2">Contact important</span>
                </li>
                <li className="cursor-pointer d-flex align-items-center">
                  <i className="ti ti-photo ti-md"></i>
                  <span className="align-middle ms-2">Fichiers partages</span>
                </li>
                <li className="cursor-pointer d-flex align-items-center">
                  <i className="ti ti-trash ti-md"></i>
                  <span className="align-middle ms-2">Supprimer le contact</span>
                </li>
                <li className="cursor-pointer d-flex align-items-center">
                  <i className="ti ti-ban ti-md"></i>
                  <span className="align-middle ms-2">Bloquer le contact</span>
                </li>
              </ul>
            </div>
            <div className="d-flex mt-6">
              <button className="btn btn-danger w-100">
                Supprimer le contact<i className="ti ti-trash ti-16px ms-2"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatHistory;
